import * as React from "react";

import {
  getDatasetLinkContext,
  getFileLinkContext,
  getMessagePathLinkContext,
  getTopicLinkContext,
  isTimestampLink,
} from "@/shared/components/RobotoMarkdown/magicLinks.ts";
import { Timestamp } from "@/shared/components/RobotoMarkdown/Timestamp.tsx";
import { deserializeTimestamp } from "@/shared/text";

export type DatasetLinkRenderer = (props: {
  datasetId: string;
  text: string;
  time?: bigint;
  onLinkClick?: () => void;
  auto?: boolean;
}) => React.ReactElement;

export type FileLinkRenderer = (props: {
  fileId: string;
  text: string;
  time?: bigint;
  onLinkClick?: () => void;
  auto?: boolean;
}) => React.ReactElement;

export type TopicLinkRenderer = (props: {
  topicId: string;
  text: string;
  time?: bigint;
  onLinkClick?: () => void;
  auto?: boolean;
}) => React.ReactElement;

export type MessagePathLinkRenderer = (props: {
  messagePathId: string;
  text: string;
  time?: bigint;
  onLinkClick?: () => void;
  auto?: boolean;
}) => React.ReactElement;

export interface RobotoMarkdownLinkProps {
  datasetLinkRenderer?: DatasetLinkRenderer;
  fileLinkRenderer?: FileLinkRenderer;
  topicLinkRenderer?: TopicLinkRenderer;
  messagePathLinkRenderer?: MessagePathLinkRenderer;
  href?: string;
  onLinkClick?: () => void;
}

const extractTextFromChildren = (children: React.ReactNode): string => {
  if (typeof children === "string") {
    return children;
  }

  if (typeof children === "number") {
    return String(children);
  }

  if (React.isValidElement(children)) {
    const element = children as React.ReactElement<{
      children?: React.ReactNode;
    }>;
    return extractTextFromChildren(element.props.children);
  }

  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join("");
  }

  return "";
};

export const RobotoMarkdownLink = ({
  datasetLinkRenderer,
  fileLinkRenderer,
  topicLinkRenderer,
  messagePathLinkRenderer,
  onLinkClick,
  href,
  children,
}: React.PropsWithChildren<RobotoMarkdownLinkProps>) => {
  const childText = extractTextFromChildren(children);

  if (href) {
    try {
      if (isTimestampLink(href)) {
        const timestamp = deserializeTimestamp(href);
        return <Timestamp timestamp={timestamp}>{children}</Timestamp>;
      }

      if (datasetLinkRenderer) {
        const datasetLinkContext = getDatasetLinkContext(href);
        if (datasetLinkContext) {
          return datasetLinkRenderer({
            datasetId: datasetLinkContext.dataset.dataset_id,
            text: childText,
            time: datasetLinkContext.dataset.t,
            auto: datasetLinkContext.dataset.auto,
            onLinkClick: onLinkClick,
          });
        }
      }

      if (fileLinkRenderer) {
        const fileLinkContext = getFileLinkContext(href);
        if (fileLinkContext) {
          return fileLinkRenderer({
            fileId: fileLinkContext.file.file_id,
            text: childText,
            time: fileLinkContext.file.t,
            auto: fileLinkContext.file.auto,
            onLinkClick: onLinkClick,
          });
        }
      }

      if (topicLinkRenderer) {
        const topicLinkContext = getTopicLinkContext(href);
        if (topicLinkContext) {
          return topicLinkRenderer({
            topicId: topicLinkContext.topic.topic_id,
            text: childText,
            time: topicLinkContext.topic.t,
            auto: topicLinkContext.topic.auto,
            onLinkClick: onLinkClick,
          });
        }
      }

      if (messagePathLinkRenderer) {
        const messagePathLinkContext = getMessagePathLinkContext(href);
        if (messagePathLinkContext) {
          return messagePathLinkRenderer({
            messagePathId: messagePathLinkContext.message_path.message_path_id,
            text: childText,
            time: messagePathLinkContext.message_path.t,
            auto: messagePathLinkContext.message_path.auto,
            onLinkClick: onLinkClick,
          });
        }
      }
    } catch {
      // Do nothing, we'll fall back to default naive rendering behavior
    }
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "var(--primary-main)", fontWeight: 500 }}
    >
      {children}
    </a>
  );
};
